import amplitudeLog from '@client/amplitude';
import { DotLoader } from '@ui/DotLoader';
import { LoadingText } from '@ui/LoadingText';
import { useInitElementsSortFilter } from '@client/hooks/use-filter-elements.hooks';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import { useProjectState } from '@client/store/project';
import { useUIState } from '@client/store/ui';
import { Page } from '@client/store/ui/ui.model';
import { AppBarStyles } from '@client/style/style.constants';
import { Box } from '@mui/material';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { ReportView } from './components/ReportView';
import { ElementsView } from './components/ElementsView';
import Calculations from './components/CalculationsView';
import DataTablesView from './components/DataTablesView/DataTablesView';
import DesignView from './components/DesignView';
import {
  AMP_APP_NAV_TO_DESIGN,
  AMP_APP_NAV_TO_ELEMENTS,
  AMP_APP_NAV_TO_PRODUCTS,
  AMP_APP_NAV_TO_REPORT,
} from '@shared/constants';

const DiffView = lazy(() => import('./components/DiffView/DiffView'));

const PAGE_NAV_EVENT_MAP: Partial<Record<Page, string>> = {
  [Page.Design]: AMP_APP_NAV_TO_DESIGN,
  [Page.Elements]: AMP_APP_NAV_TO_ELEMENTS,
  [Page.Products]: AMP_APP_NAV_TO_PRODUCTS,
  [Page.Report]: AMP_APP_NAV_TO_REPORT,
};

const ProjectView: React.FC = () => {
  const { selectedPage } = useUIState('selectedPage');
  const prevSelectedPage = usePrevious(selectedPage);
  const { project, projectTarget, isLoading } = useProjectState(
    'project',
    'projectTarget',
    'isLoading',
  );
  useInitElementsSortFilter();

  useEffect(() => {
    if (prevSelectedPage !== selectedPage) {
      const event = PAGE_NAV_EVENT_MAP[selectedPage];
      if (!event) return;
      amplitudeLog(event);
    }
  }, [prevSelectedPage, selectedPage]);

  const isCorrectTarget = (() => {
    switch (projectTarget.type) {
      case 'id':
        return projectTarget.id === project.id;
      case 'shared':
      case 'generation':
        return project.id !== 0;
      case 'none':
        return false;
    }
  })();

  const pageView = useMemo(() => {
    switch (selectedPage) {
      case Page.Elements:
      case Page.Products:
      case Page.Import:
        return <ElementsView />;
      case Page.Diff:
        return <DiffView />;
      case Page.Calculations:
        return <Calculations />;
      case Page.Report:
        return <ReportView />;
      case Page.DataTables:
        return <DataTablesView />;
      case Page.Design:
        return <DesignView />;
      default:
        return assertUnreachable(selectedPage);
    }
  }, [selectedPage]);

  if (isLoading && !isCorrectTarget) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'fixed',
          top: AppBarStyles.HEIGHT,
          bottom: 0,
          left: 0,
          right: 0,
          height: 'auto',
          bgcolor: 'warmSurface.main',
        }}
      >
        <LoadingText text="Loading project" />
      </Box>
    );
  }

  return (
    <Suspense
      fallback={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          Loading {selectedPage}
          <DotLoader />
        </Box>
      }
    >
      <Box data-project-opening-content="" sx={{ width: '100%' }}>
        {pageView}
      </Box>
    </Suspense>
  );
};

export default ProjectView;
